import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Download, ArrowRight, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * /gidsen — public landing page listing every downloadable guide.
 *
 * Purely dynamic: whatever the admin uploads in /admin → Gidsen shows
 * up here. Nothing is hardcoded — no "coming soon" placeholders, no
 * featured cards. If the list is empty we show a graceful, on-brand
 * "we're working on it" state instead of pretending.
 */
const Gidsen = () => {
  const [guides, setGuides] = useState(null); // null = loading, [] = empty

  useEffect(() => {
    fetch(`${API_URL}/api/gidsen`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setGuides(d.items || []))
      .catch(() => setGuides([]));
  }, []);

  return (
    <div style={styles.wrap} data-testid="gidsen-page">
      <Helmet>
        <title>Gratis Gidsen — Alles over kopen in Spanje | CasaOthers</title>
        <meta
          name="description"
          content="Download gratis onze praktische gidsen over het kopen van een woning in Spanje. Checklists, kostencalculators en experttips — direct in je inbox."
        />
        <link rel="canonical" href="https://casaothers.nl/gidsen" />
        <meta property="og:title" content="Gratis Gidsen — CasaOthers" />
        <meta
          property="og:description"
          content="Praktische gidsen, checklists en tools voor jouw woningaankoop in Spanje."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://casaothers.nl/gidsen" />
      </Helmet>

      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.tag} data-testid="gidsen-tag">
          <Sparkles size={14} /> Gratis kennis
        </div>
        <h1 style={styles.h1}>Onze gidsen voor kopers in Spanje</h1>
        <p style={styles.lead}>
          Wij bundelen jaren praktijkervaring in korte, doorleesbare gidsen —
          zodat jij zonder verrassingen jouw droomhuis in Spanje koopt.
          Elke gids is gratis en direct te downloaden.
        </p>
      </div>

      {/* Guides — dynamic only */}
      {guides === null && (
        <div style={styles.stateBox} data-testid="gidsen-loading">
          <p style={styles.stateText}>Gidsen laden…</p>
        </div>
      )}

      {guides && guides.length === 0 && (
        <div style={styles.stateBox} data-testid="gidsen-empty">
          <BookOpen size={32} color="#C5A059" />
          <h2 style={styles.stateTitle}>Nieuwe gidsen op komst</h2>
          <p style={styles.stateText}>
            We werken aan onze eerste gidsen. Kom binnenkort terug — of neem
            contact op als je een specifiek onderwerp wilt zien.
          </p>
        </div>
      )}

      {guides && guides.length > 0 && (
        <div style={styles.grid}>
          {guides.map((g) => (
            <a
              key={g.id}
              href={`/download/${g.slug}`}
              style={styles.card}
              data-testid={`guide-${g.slug}`}
              className="gidsen-card"
            >
              <div style={styles.cardHead}>
                <BookOpen size={28} color="#C5A059" />
                <span style={styles.cardMeta}>PDF</span>
              </div>
              <h2 style={styles.cardTitle}>{g.title_nl}</h2>
              {g.description_nl && (
                <p style={styles.cardBody}>{g.description_nl}</p>
              )}
              <div style={styles.cardCTA}>
                <Download size={16} />
                <span>Download gratis</span>
                <ArrowRight size={16} style={{ marginLeft: 'auto' }} />
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Hover styling via CSS-in-head so we don't touch global sheets */}
      <style>{`
        .gidsen-card {
          transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
        }
        .gidsen-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px -20px rgba(26, 25, 24, 0.35);
          border-color: #C5A059;
        }
      `}</style>
    </div>
  );
};

const styles = {
  wrap: { maxWidth: '1180px', margin: '0 auto', padding: '3.5rem 1.5rem 5rem' },
  hero: { maxWidth: '760px', margin: '0 auto 3rem', textAlign: 'center' },
  tag: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.35rem 0.9rem', backgroundColor: '#FEF3C7', color: '#78350F',
    border: '1px solid #FCD34D', borderRadius: '999px', fontSize: '0.8rem',
    fontWeight: 600, marginBottom: '1.25rem',
  },
  h1: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2rem, 5vw, 3.2rem)',
    color: '#1A1918', margin: '0 0 1rem', lineHeight: 1.15,
  },
  lead: { color: '#585755', fontSize: '1.05rem', lineHeight: 1.65, margin: 0 },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    alignItems: 'stretch',
  },

  card: {
    position: 'relative',
    display: 'flex', flexDirection: 'column',
    padding: '2rem 1.75rem',
    backgroundColor: '#FFFFFF',
    border: '1px solid #EAE5DD',
    color: '#1A1918',
    textDecoration: 'none',
    minHeight: '260px',
  },
  cardHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '1.25rem',
  },
  cardMeta: { fontSize: '0.75rem', color: '#8C8B88', letterSpacing: '0.05em', textTransform: 'uppercase' },
  cardTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem', lineHeight: 1.25, margin: '0 0 0.85rem',
    color: '#1A1918',
  },
  cardBody: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1rem' },
  cardCTA: {
    marginTop: 'auto',
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.85rem 1rem',
    backgroundColor: '#1A1918', color: '#C5A059',
    fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  // Empty / loading state — kept minimal so it feels intentional, not broken
  stateBox: {
    maxWidth: '520px', margin: '2rem auto', padding: '3rem 2rem',
    textAlign: 'center', background: '#FAF9F6', border: '1px dashed #D8D3CA',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
  },
  stateTitle: {
    fontFamily: "'Playfair Display', serif", fontSize: '1.5rem',
    color: '#1A1918', margin: 0,
  },
  stateText: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 },
};

export default Gidsen;
